import { Button, CircularProgress, Menu, MenuItem, SvgIcon } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useId, useRef, useState } from 'react';
import {
    createAppExportWorkbook,
    printAppExport,
    projectAppExport,
    serializeAppExportCsv,
    type AppExportProjection,
} from './AppTable.export';
import type { AppExportContext } from './AppTable.types';

function fileNameFor(projection: AppExportProjection, extension: 'csv' | 'xlsx'): string {
    const name = (projection.fileName || projection.title || 'Export').trim().replace(/\.(csv|xlsx)$/i, '') || 'Export';

    return `${name}.${extension}`;
}

export function downloadAppExportBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);

    try {
        anchor.click();
    } finally {
        anchor.remove();
        globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
}

export interface AppTableToolbarProps<TRow> {
    context: AppExportContext<TRow>;
}

export function AppTableToolbar<TRow>({ context }: AppTableToolbarProps<TRow>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [excelPending, setExcelPending] = useState(false);
    const excelPendingRef = useRef(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const id = useId();
    const menuId = `${id}-export-menu`;
    const menuOpen = Boolean(anchorEl);

    const closeMenu = () => setAnchorEl(null);

    const exportCsv = () => {
        closeMenu();

        try {
            const projection = projectAppExport(context);

            downloadAppExportBlob(
                new Blob([serializeAppExportCsv(projection)], {
                    type: 'text/csv;charset=utf-8',
                }),
                fileNameFor(projection, 'csv'),
            );
        } catch {
            enqueueSnackbar('Unable to export CSV.', {
                variant: 'error',
                preventDuplicate: true,
            });
        }
    };

    const exportExcel = async () => {
        if (excelPendingRef.current) {
            return;
        }
        closeMenu();
        excelPendingRef.current = true;
        setExcelPending(true);

        try {
            const projection = projectAppExport(context);
            const buffer = await createAppExportWorkbook(projection);

            downloadAppExportBlob(
                new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
                fileNameFor(projection, 'xlsx'),
            );
        } catch {
            enqueueSnackbar('Unable to export Excel workbook.', {
                variant: 'error',
                preventDuplicate: true,
            });
        } finally {
            excelPendingRef.current = false;
            setExcelPending(false);
        }
    };

    const print = () => {
        closeMenu();

        try {
            const projection = projectAppExport(context);

            if (!printAppExport(projection)) {
                enqueueSnackbar('Unable to open the print window.', {
                    variant: 'error',
                    preventDuplicate: true,
                });
            }
        } catch {
            enqueueSnackbar('Unable to prepare the print view.', {
                variant: 'error',
                preventDuplicate: true,
            });
        }
    };

    return (
        <>
            <Button
                ref={triggerRef}
                id={`${id}-export-button`}
                size="small"
                aria-label="Export"
                aria-haspopup="menu"
                aria-expanded={menuOpen ? 'true' : 'false'}
                aria-controls={menuOpen ? menuId : undefined}
                aria-busy={excelPending}
                disabled={excelPending}
                startIcon={
                    excelPending ? (
                        <CircularProgress color="inherit" size={14} aria-label="Preparing Excel export" />
                    ) : (
                        <SvgIcon fontSize="small" aria-hidden="true">
                            <path d="M5 20h14v-2H5v2zm7-18v12l4-4 1.41 1.41L12 19.83 6.59 11.4 8 10l3 4V2h1z" />
                        </SvgIcon>
                    )
                }
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                {excelPending ? 'Preparing…' : 'Export'}
            </Button>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    transition: {
                        onExited: () => triggerRef.current?.focus(),
                    },
                }}
                MenuListProps={{
                    'aria-labelledby': `${id}-export-button`,
                }}
            >
                <MenuItem aria-label="Export CSV" onClick={exportCsv}>
                    Download as CSV
                </MenuItem>
                <MenuItem
                    aria-label="Export Excel"
                    disabled={excelPending}
                    onClick={() => {
                        void exportExcel();
                    }}
                >
                    Download as Excel
                </MenuItem>
                <MenuItem aria-label="Print table" onClick={print}>
                    Print
                </MenuItem>
            </Menu>
        </>
    );
}

export default AppTableToolbar;
